// "Add your testimonial" modal. Logged-in students only — text, voice, or an
// external video link. Bilingual (EN + Amharic) and explicitly invites students
// to share in their own language. Lands as 'pending' for review.

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { testimonialsAPI } from '../services/testimonials';
import { STRINGS } from '../i18n/strings';
import VoiceRecorder from './feedback/VoiceRecorder';

const en = (k: string) => (STRINGS.en as any)[k] || '';
// '' when there's no dedicated Amharic string → we render Amharic only where it exists.
const amOpt = (k: string) => (STRINGS.am as any)[k] || '';

interface Props {
  onClose: () => void;
  onSubmitted: () => void;
}

type Mode = 'text' | 'voice' | 'link';

const AddTestimonialModal: React.FC<Props> = ({ onClose, onSubmitted }) => {
  const { student, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState<Mode>('text');
  const [quote, setQuote] = useState('');
  const [link, setLink] = useState('');
  const [authorName, setAuthorName] = useState(student?.display_name || student?.first_name || '');
  const [school, setSchool] = useState(student?.school_name || '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // Compose the "Grade · School" detail line from profile grade + the school field.
  const detail = [student?.grade_level ? `Grade ${student.grade_level}` : '', school.trim()]
    .filter(Boolean)
    .join(' · ');

  if (!isAuthenticated) {
    return (
      <div className="t-modal-backdrop" onClick={onClose}>
        <div className="t-modal" onClick={(e) => e.stopPropagation()}>
          <button className="t-modal-close" onClick={onClose} aria-label="Close">×</button>
          <h2>{en('testimonials.modal.title')}</h2>
          {amOpt('testimonials.modal.title') && <p className="t-modal-am">{amOpt('testimonials.modal.title')}</p>}
          <p className="t-modal-sub">{en('testimonials.login.sub')}</p>
          <button className="t-btn-primary" onClick={() => navigate('/login')}>{en('testimonials.login.cta')}</button>
        </div>
      </div>
    );
  }

  const handleError = (e: any) => {
    setError(e?.message === 'AUTH' ? 'Please log in again to submit.' : (e?.message || 'Something went wrong. Please try again.'));
  };
  const finish = () => { setDone(true); setSubmitting(false); onSubmitted(); };

  // Submitting IS the consent — no checkbox; a quiet notice explains it.
  const submitText = async () => {
    if (mode === 'text' && !quote.trim()) { setError('Write a short message.'); return; }
    if (mode === 'link' && !link.trim()) { setError('Paste your video link.'); return; }
    setSubmitting(true); setError(null);
    try {
      await testimonialsAPI.submit({
        quote: quote.trim() || undefined,
        external_url: mode === 'link' ? link.trim() : undefined,
        author_name: authorName.trim() || undefined,
        author_detail: detail || undefined,
        consent: true,
      });
      finish();
    } catch (e) { handleError(e); setSubmitting(false); }
  };

  const submitVoice = async (blob: Blob, seconds: number) => {
    setSubmitting(true); setError(null);
    try {
      await testimonialsAPI.submitVoice(blob, {
        author_name: authorName.trim() || undefined,
        author_detail: detail || undefined,
        duration_s: Math.round(seconds),
        consent: true,
      });
      finish();
    } catch (e) { handleError(e); setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="t-modal-backdrop" onClick={onClose}>
        <div className="t-modal" onClick={(e) => e.stopPropagation()}>
          <h2>{en('testimonials.thanks.title')}</h2>
          {amOpt('testimonials.thanks.title') && <p className="t-modal-am">{amOpt('testimonials.thanks.title')}</p>}
          <p className="t-modal-sub">{en('testimonials.thanks.body')}</p>
          <button className="t-btn-primary" onClick={onClose}>{en('common.close')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="t-modal-backdrop" onClick={onClose}>
      <div className="t-modal" onClick={(e) => e.stopPropagation()}>
        <button className="t-modal-close" onClick={onClose} aria-label="Close">×</button>
        <h2>{en('testimonials.modal.title')}</h2>
        <p className="t-modal-sub">{en('testimonials.modal.sub')}</p>

        <div className="t-mode-tabs">
          <button className={mode === 'text' ? 'active' : ''} onClick={() => setMode('text')}>{en('testimonials.tab.text')}</button>
          <button className={mode === 'voice' ? 'active' : ''} onClick={() => setMode('voice')}>{en('testimonials.tab.voice')}</button>
          <button className={mode === 'link' ? 'active' : ''} onClick={() => setMode('link')}>{en('testimonials.tab.link')}</button>
        </div>

        {mode === 'text' && (
          <textarea className="t-input" rows={4} placeholder={en('testimonials.placeholder.text')}
            value={quote} onChange={(e) => setQuote(e.target.value)} maxLength={4000} />
        )}

        {mode === 'link' && (
          <>
            <input className="t-input" placeholder={en('testimonials.placeholder.link')}
              value={link} onChange={(e) => setLink(e.target.value)} />
            <textarea className="t-input" rows={2} placeholder="Add a caption (optional)"
              value={quote} onChange={(e) => setQuote(e.target.value)} maxLength={2000} />
          </>
        )}

        {mode === 'voice' && (
          <div className="t-voice-wrap">
            <p className="t-hint">{en('testimonials.voice.hint')}</p>
            <VoiceRecorder onComplete={submitVoice} onError={(m) => setError(m)} maxSeconds={60} disabled={submitting} />
          </div>
        )}

        <div className="t-author-row">
          <input className="t-input" value={authorName}
            placeholder={en('testimonials.field.name') + (amOpt('testimonials.field.name') ? ' · ' + amOpt('testimonials.field.name') : '')}
            onChange={(e) => setAuthorName(e.target.value)} maxLength={120} />
          <input className="t-input" placeholder={en('testimonials.field.school')} value={school}
            onChange={(e) => setSchool(e.target.value)} maxLength={160} />
        </div>

        <p className="t-consent-note">{en('testimonials.consent')}</p>

        {error && <div className="t-error">{error}</div>}

        {mode !== 'voice' && (
          <button className="t-btn-primary" onClick={submitText} disabled={submitting}>
            {submitting ? en('testimonials.submitting') : en('testimonials.submit')}
          </button>
        )}
      </div>
    </div>
  );
};

export default AddTestimonialModal;
